import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShareButtons } from "@/components/site/ShareButtons";
import { CommentSection } from "@/components/site/CommentSection";
import { BrandedImage } from "@/components/site/BrandedImage";
import { Clock, Eye, ArrowLeft } from "lucide-react";

export default function ArticolDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/${slug}`).then(r => setA(r.data)).catch(() => setA(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-32 text-[#A0AABF]">Se încarcă...</div>;
  if (!a) return <div className="max-w-3xl mx-auto px-4 py-32 text-[#A0AABF]">Articolul nu a fost găsit.</div>;

  return (
    <article data-testid="page-article-detail" className="pb-20">
      <div className="relative h-[55vh] md:h-[70vh] overflow-hidden">
        <BrandedImage
          src={a.hero_image}
          alt={a.title}
          className="absolute inset-0 w-full h-full hero-scale"
          watermarkPosition="br"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1020] via-[#0A1020]/60 to-[#0A1020]/20 pointer-events-none z-[5]" />
        <div className="absolute inset-x-0 bottom-0 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 z-10">
          <Link to="/articole" className="inline-flex items-center gap-2 text-[#D4A63D] text-sm hover:text-[#F2C053] mb-6">
            <ArrowLeft className="w-4 h-4" /> Toate articolele
          </Link>
          <span className="inline-block text-[10px] uppercase tracking-[0.3em] text-[#D4A63D] bg-[#0A1020]/85 px-3 py-1 border border-[#D4A63D]/30 mb-4">{a.category}</span>
          <h1 className="font-display text-3xl md:text-5xl lg:text-6xl text-[#F8F8F4] leading-[1.1]">{a.title}</h1>
          <div className="mt-6 flex items-center gap-6 text-xs text-[#A0AABF] uppercase tracking-widest">
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {a.reading_time} min</span>
            <span className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> {a.views_count?.toLocaleString("ro-RO")}</span>
            <span>{a.author_name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <p className="font-quote text-2xl md:text-3xl italic text-[#D4A63D]/90 leading-relaxed border-l-2 border-[#D4A63D] pl-6">{a.excerpt}</p>

        <div className="prose prose-invert max-w-none mt-10">
          {(a.content || "").split("\n\n").map((p, i) => (
            <p key={i} className="text-[#F8F8F4]/90 text-base md:text-lg leading-relaxed mb-5 whitespace-pre-line">{p}</p>
          ))}
        </div>

        <div className="my-12 gold-divider" />

        <div className="cinematic-card rounded-lg p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-sm text-[#A0AABF] mb-2">Articolul te-a atins? Distribuie-l.</p>
            <p className="font-quote text-xl text-[#F8F8F4]">„Cel mai bun mod de a schimba ceva este să-l facem cunoscut.”</p>
          </div>
          <ShareButtons title={a.title} compact />
        </div>

        <div className="mt-12 grid sm:grid-cols-2 gap-4">
          <Link to="/ajutor" className="cinematic-card rounded-lg p-5 group">
            <div className="text-xs uppercase tracking-widest text-[#D4A63D] mb-2">Următorul pas</div>
            <div className="font-display text-lg text-[#F8F8F4] group-hover:text-[#D4A63D]">Cer ajutor pentru situația mea</div>
          </Link>
          <Link to="/programare" className="cinematic-card rounded-lg p-5 group">
            <div className="text-xs uppercase tracking-widest text-[#D4A63D] mb-2">Sprijin direct</div>
            <div className="font-display text-lg text-[#F8F8F4] group-hover:text-[#D4A63D]">Vorbește cu Gabriel</div>
          </Link>
        </div>

        <CommentSection targetType="article" targetId={a.slug} />
      </div>
    </article>
  );
}
